import { Modal } from 'antd';
import type { ModalFuncProps } from 'antd';
import { TAP_GUARD_MS } from './tapGuard';

type Thenable = { then: (...args: Array<unknown>) => unknown };
const isThenable = (v: unknown): v is Thenable =>
  !!v && typeof (v as { then?: unknown }).then === 'function';

type Open = {
  handle: ReturnType<typeof Modal.confirm>;
  /** The page it was opened on: a move to another page closes it (closeFloorConfirmsOff). */
  path: string;
  /** Its OK or Cancel is running now: a confirm that act opens is the next step, not a second dialog. */
  acting: boolean;
  /** Closing (or closed): it no longer stands in the way of the next confirm. */
  closing: boolean;
};

/** The floor confirm on screen now (undefined: none open). */
let current: Open | undefined;
/** When the last floor confirm closed, and when the shield after it last swallowed a tap. */
const after = { closedAt: undefined as number | undefined, swallowedAt: undefined as number | undefined };

const currentPath = () => (typeof window === 'undefined' ? '' : window.location.pathname);

/**
 * The taps of the thumb that closed a confirm land on what was under it (round 39, Imran: the second tap of a double
 * tap on "Start new GRN" went through to the PO list behind and opened "Start a new GRN for PO/PLEAT/…?"). For
 * TAP_GUARD_MS after a confirm closes, a pointer tap anywhere is swallowed, and so is a tap that keeps a streak of
 * swallowed taps going. A keyboard's Enter (`detail` 0) is deliberate and goes through.
 */
const shieldAfterClose = (e: MouseEvent) => {
  if ((e.detail ?? 0) < 1) return;
  const t = Date.now();
  const fresh = after.closedAt !== undefined && t - after.closedAt < TAP_GUARD_MS;
  const streak = after.swallowedAt !== undefined && t - after.swallowedAt < TAP_GUARD_MS;
  if (!fresh && !streak) return;
  after.swallowedAt = t;
  e.stopPropagation();
  e.preventDefault();
};
let shieldInstalled = false;
const installShield = () => {
  if (shieldInstalled || typeof document === 'undefined') return;
  shieldInstalled = true;
  document.addEventListener('click', shieldAfterClose, true);
};

/**
 * Closes the floor confirm left open from another page (round 39, Imran: a confirm for another PO stayed on top of the
 * new GRN screen). The Hub calls it when the page changes; a confirm the new page opened as it mounted stays.
 */
export const closeFloorConfirmsOff = (path: string = currentPath()) => {
  if (current && !current.closing && current.path !== path) current.handle.destroy();
};

/** The dialog leaves the screen now: what was under it gets no tap for TAP_GUARD_MS (shieldAfterClose). */
const markClosing = (entry: Open) => {
  entry.closing = true;
  after.closedAt = Date.now();
};

/**
 * A confirm on a floor screen (rule 19: a double tap is one tap). Round 25, Deepa: a fast double tap on "Add 3 to 001"
 * opened the confirm and the second tap closed it again, so nothing saved and nothing was said. The dialog grew out of
 * the tapped spot (antd's zoom from the pointer), so the second tap landed on its Cancel. Here:
 * - the dialog appears in place at once, never out of the finger's spot;
 * - its buttons take no tap for TAP_GUARD_MS after it opened;
 * - one dialog at a time: a call while one is open opens nothing (round 38: a tap and a typed PO opened two), unless
 *   the open one's own OK or Cancel opens it as the next step;
 * - a tap on the mask never closes it;
 * - for TAP_GUARD_MS after it closes, a tap reaches nothing under it (shieldAfterClose);
 * - moving to another page closes it (closeFloorConfirmsOff).
 * Same arguments and return as Modal.confirm.
 */
export const floorConfirm = (config: ModalFuncProps): ReturnType<typeof Modal.confirm> => {
  installShield();
  // The one on screen is the answer (a second tap, a scan and a tap at once).
  if (current && !current.closing && !current.acting)
    return { destroy: () => undefined, update: () => undefined };
  const opened = Date.now();
  const tooSoon = () => Date.now() - opened < TAP_GUARD_MS;
  const { onOk, onCancel, afterClose } = config;
  const entry: Open = {
    handle: undefined as never,
    path: currentPath(),
    acting: false,
    closing: false,
  };
  /** Runs OK's or Cancel's act; its promise keeps the dialog open (antd shows it loading) until it settles. */
  const act = (fn: (() => unknown) | undefined, close: () => void) => {
    if (tooSoon()) return undefined;
    entry.acting = true;
    let result: unknown;
    try {
      result = fn?.();
    } catch (e) {
      entry.acting = false;
      throw e;
    }
    if (isThenable(result)) {
      // antd closes the dialog once the act succeeds, and keeps it open when the act fails.
      (result as Promise<unknown>).then(
        () => {
          entry.acting = false;
          markClosing(entry);
        },
        () => {
          entry.acting = false;
        },
      );
      return result;
    }
    entry.acting = false;
    markClosing(entry);
    close();
    return undefined;
  };
  const handle = Modal.confirm({
    ...config,
    maskClosable: false,
    transitionName: '',
    maskTransitionName: '',
    // A function taking `close` is called by antd with it and closes nothing by itself: the guard decides.
    onOk: (close: () => void) => act(onOk, close),
    onCancel: (close: () => void) => act(onCancel, close),
    afterClose: () => {
      if (current === entry) current = undefined;
      markClosing(entry);
      afterClose?.();
    },
  });
  entry.handle = {
    ...handle,
    destroy: () => {
      if (!entry.closing) markClosing(entry);
      handle.destroy();
    },
  };
  current = entry;
  return entry.handle;
};
